import axios from 'axios';
import { queryClient } from '../queryClient';
import { clearAuthAndTenantStorage } from '../utils/authStorage';
import { extractSubdomain, getTenantHintForApiRequest } from '../utils/tenantResolver';
import { SwaggerAiAnalyzeResponse, SwaggerAiExecuteLoginResponse, SwaggerAiRunSummaryResponse, SwaggerImportRequest, SwaggerImportResponse, SwaggerPreviewData } from '../types/swagger';
import { RequestDataSet, RequestDataSetResult } from '../types/dataSets';
import { RequestAssertion, AssertionValidationRequest, AssertionSuggestionRequest } from '../types/assertions';
import { normalizeAssertion, normalizeAssertionList } from '../utils/assertionOperatorConfig';
import { HelpArticle, HelpArticleType, HelpChatResponse, HelpModule, HelpSubModule } from '../types/help';

// Determine API base URL based on environment
const origin = window.location.origin;
const hostname = window.location.hostname;
const isLocalhost = origin.includes('localhost') || origin.includes('127.0.0.1');
const isPrivateIp =
  hostname.startsWith('192.168.') ||
  hostname.startsWith('10.') ||
  hostname.startsWith('172.16.') ||
  hostname.startsWith('172.17.') ||
  hostname.startsWith('172.18.') ||
  hostname.startsWith('172.19.') ||
  hostname.startsWith('172.2') ||
  hostname.startsWith('172.3');

// Configuration: If your backend is on a different server/port than the frontend,
// specify it here. Leave empty to use same-origin (requires reverse proxy).
const REMOTE_BACKEND_URL = origin.includes('api.dragonflytest.in')
  ? 'http://api.dragonflytest.in:8080/api'
  : origin.includes('crudexpert.com')
    ? `${window.location.protocol}//${window.location.hostname}/api`
    : '';

export const getApiBaseUrl = (): string => {
  if (isLocalhost) {
    return 'http://localhost:8080/api';
  } else if (isPrivateIp) {
    // LAN access - use same host with backend port
    return `http://${hostname}:8080/api`;
  } else {
    // Remote server - use custom backend URL if specified, otherwise same-origin
    // If using same-origin, you MUST set up a reverse proxy on the server
    // to forward /api/* requests to your Spring Boot backend
    return REMOTE_BACKEND_URL || `${origin}/api`;
  }
};

/** Ensure OpenAPI schema fields are JSON strings for create/update request APIs. */
const normalizeRequestSchemaFields = <T extends Record<string, unknown>>(request: T): T => {
  const payload = { ...request } as T & {
    requestBodySchema?: unknown;
    responseSchema?: unknown;
  };
  if (payload.requestBodySchema != null && typeof payload.requestBodySchema !== 'string') {
    payload.requestBodySchema = JSON.stringify(payload.requestBodySchema);
  }
  if (payload.responseSchema != null && typeof payload.responseSchema !== 'string') {
    payload.responseSchema = JSON.stringify(payload.responseSchema);
  }
  return payload as T;
};

const API_BASE_URL = getApiBaseUrl();
const DASHBOARD_RECENTS_EVENT = 'dashboard-recent-updated';
const TEST_PLAN_DATASETS_UPDATED_EVENT = 'test-plan-datasets-updated';
export const AUTH_SESSION_INVALIDATED_EVENT = 'auth:session-invalidated';
const ENABLE_API_DEBUG_LOGS = process.env.REACT_APP_API_DEBUG_LOGS === 'true';

/** Invalidate Test Plan details cache + notify open Test Plan pages after Collections data-set changes. */
async function notifyTestPlanDataSetsUpdated(): Promise<void> {
  try {
    const { invalidateAllTestCycleDetailsCache } = await import('./testCycleService');
    invalidateAllTestCycleDetailsCache();
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TEST_PLAN_DATASETS_UPDATED_EVENT));
  }
}

/** Clear client auth and redirect to login when the server invalidates this session (e.g. login elsewhere). */
function forceClientLogout(errorCode: string | undefined): void {
  try {
    sessionStorage.setItem('sessionReplacedRedirectedAt', String(Date.now()));
    if (errorCode === 'SESSION_REPLACED') {
      sessionStorage.setItem('sessionReplacedNotice', '1');
    } else {
      sessionStorage.removeItem('sessionReplacedNotice');
    }
  } catch {
    /* ignore */
  }
  clearAuthAndTenantStorage(queryClient);
  window.dispatchEvent(
    new CustomEvent(AUTH_SESSION_INVALIDATED_EVENT, { detail: { errorCode } }),
  );
  if (errorCode === 'SESSION_REPLACED') {
    window.location.href = '/login?sessionReplaced=1';
  } else {
    window.location.href = '/login';
  }
}

const notifyDashboardRecentsUpdated = () => {
  window.dispatchEvent(new Event(DASHBOARD_RECENTS_EVENT));
};

export const API_EXECUTION_USAGE_EVENT = 'api-execution-usage-updated';

const notifyApiExecutionUsageUpdated = () => {
  window.dispatchEvent(new Event(API_EXECUTION_USAGE_EVENT));
};

export const DEFAULT_API_EXECUTION_LIMIT_MESSAGE =
  'API execution limit exceeded for this month. Add an execution pack from Subscription add-ons.';

/** User-facing message when monthly API execution quota is exhausted, or null if err is unrelated. */
export const getApiExecutionLimitMessage = (err: unknown): string | null => {
  const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
  const msg = String(data?.error ?? data?.message ?? (err as Error)?.message ?? '').trim();
  if (!msg) return null;
  const lower = msg.toLowerCase();
  const isLimit =
    (lower.includes('api execution') || lower.includes('execution limit')) &&
    (lower.includes('limit') ||
      lower.includes('reached') ||
      lower.includes('exceeded') ||
      lower.includes('execution pack'));
  return isLimit ? msg : null;
};

/** True when the backend rejected an API execution due to monthly quota (plan + add-ons). */
export const isApiExecutionLimitError = (err: unknown): boolean =>
  getApiExecutionLimitMessage(err) != null;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let subscriptionExpiredHandler: (() => void) | null = null;

/** Set from `/user/me` so we only show the expired popup when that source agrees (avoids false positives on login). */
let subscriptionExpiredConfirmedByUserMe = false;

export const setSubscriptionExpiredConfirmedByUserMe = (value: boolean) => {
  subscriptionExpiredConfirmedByUserMe = value;
};

export const setSubscriptionExpiredHandler = (handler: (() => void) | null) => {
  subscriptionExpiredHandler = handler;
  return () => {
    if (subscriptionExpiredHandler === handler) {
      subscriptionExpiredHandler = null;
    }
  };
};

const BULK_DATASET_CHUNK_SIZE = 100;

function mapRequestVariableDto(v: any): any {
  if (!v || typeof v !== 'object') return v;
  const scope =
    v.scope != null && typeof v.scope === 'string' ? v.scope.toLowerCase() : v.scope;
  return { ...v, scope };
}

function mapRequestVariableList(list: any[]): any[] {
  if (!Array.isArray(list)) return list;
  return list.map(mapRequestVariableDto);
}

// Add request interceptor to include JWT token and tenant context
apiClient.interceptors.request.use(
  (config) => {
    // For FormData (multipart/form-data), remove Content-Type header FIRST to let browser set it with boundary
    if (config.data instanceof FormData) {
      // Remove Content-Type header completely so browser can set it with boundary
      if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
      // Also remove from common headers if it exists
      if (config.headers.common) {
        delete config.headers.common['Content-Type'];
        delete config.headers.common['content-type'];
      }
    }

    const token = localStorage.getItem('token');
    const requestUrl = config.url || '';
    const skipAuthHeader =
      requestUrl.includes('/auth/exchange-token') ||
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/force-login');
    /** Do not send a previous session's org on auth calls — backend would treat it as tenant login and reject with "not a member". */
    const skipTenantFromLocalStorage =
      requestUrl.includes('/auth/exchange-token') ||
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/force-login') ||
      requestUrl.includes('/auth/send-auth-otp');
    if (token && !skipAuthHeader) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (skipAuthHeader && config.headers) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }

    if (skipTenantFromLocalStorage && config.headers) {
      // Never send a previous session's org on auth calls — but DO send the workspace
      // from the current URL so login binds to that tenant (backend reads X-Tenant-Slug first).
      delete config.headers['X-Tenant-Id'];
      delete config.headers['x-tenant-id'];
      delete config.headers['X-Tenant-Slug'];
      delete config.headers['x-tenant-slug'];
      const authTenantHint = getTenantHintForApiRequest();
      if (authTenantHint) {
        config.headers['X-Tenant-Slug'] = authTenantHint;
      }
    }

    // Add tenant context headers
    // Priority: JWT token (preferred) > localStorage > subdomain
    const hostSlug = extractSubdomain(window.location.hostname);
    const userStr = localStorage.getItem('user');
    if (userStr && !skipTenantFromLocalStorage) {
      try {
        const user = JSON.parse(userStr);
        const userSlug =
          typeof user.organizationSlug === 'string' ? user.organizationSlug.trim().toLowerCase() : '';
        const slugMatchesHost =
          !hostSlug || !userSlug || userSlug === hostSlug.toLowerCase();
        if (slugMatchesHost && user.organizationId) {
          config.headers['X-Tenant-Id'] = user.organizationId.toString();
        }
        if (user.organizationSlug) {
          config.headers['X-Tenant-Slug'] = user.organizationSlug;
        }
      } catch (error) {
        console.error('Error parsing user data for tenant context:', error);
      }
    }

    // On tenant subdomains, URL host wins over stale user.organizationSlug / dev_tenant.
    if (hostSlug) {
      config.headers['X-Tenant-Slug'] = hostSlug;
    }

    // Development mode: allow query parameter override when not already set
    if (process.env.NODE_ENV === 'development' && !config.headers['X-Tenant-Slug']) {
      const params = new URLSearchParams(window.location.search);
      const queryTenant = params.get('tenant');
      if (queryTenant) {
        config.headers['X-Tenant-Slug'] = queryTenant;
      }
    }

    if (ENABLE_API_DEBUG_LOGS) {
      console.log('API Request:', config.method?.toUpperCase(), config.url, {
        hasToken: !!token,
        tenantSlug: config.headers['X-Tenant-Slug'],
        tenantId: config.headers['X-Tenant-Id']
      });
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
apiClient.interceptors.response.use(
  (response) => {
    if (ENABLE_API_DEBUG_LOGS) {
      console.log('API Response:', response.status, response.config.method?.toUpperCase(), response.config.url);
    }
    return response;
  },
  (error) => {
    console.error('API Error:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      hasResponse: !!error.response,
      hasRequest: !!error.request
    });
    
    // Don't clear tokens for payment endpoints (they're public)
    const url = error.config?.url || error.config?.baseURL + error.config?.url || '';
    const isPaymentEndpoint = url.includes('/payment/success') || 
                              url.includes('/payment/license-success') || 
                              url.includes('/payment/api-execution-addon-success') ||
                              url.includes('/payment/tenant-addon-success') ||
                              url.includes('/payment/cancel') ||
                              url.includes('/payment/webhook');
    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/send-auth-otp') ||
      url.includes('/auth/verify-otp') ||
      url.includes('/auth/exchange-token') ||
      url.includes('/auth/force-login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password');
    
    const errorCode = error.response?.data?.errorCode;

    const isSubscriptionExpired = error.response?.data?.error === 'SUBSCRIPTION_EXPIRED';
    const isUnauthorized = error.response?.status === 401;
    const isForbiddenAuthFailure =
      error.response?.status === 403 &&
      (errorCode === 'SESSION_REPLACED' || errorCode === 'INVALID_TOKEN' || errorCode === 'TOKEN_EXPIRED');

    if (isSubscriptionExpired) {
      if (subscriptionExpiredConfirmedByUserMe) {
        subscriptionExpiredHandler?.();
      }
      return Promise.reject(error);
    }

    const pathname = window.location.pathname || '';
    const onAuthPage =
      pathname === '/login' ||
      pathname.startsWith('/login/') ||
      pathname === '/register' ||
      pathname.startsWith('/register/') ||
      pathname === '/forgot-password' ||
      pathname.startsWith('/forgot-password/');
    let authHandoffActive = false;
    try {
      const untilRaw = sessionStorage.getItem('auth_handoff_until');
      const until = untilRaw ? Number(untilRaw) : 0;
      authHandoffActive = Number.isFinite(until) && until > Date.now();
    } catch {
      /* ignore */
    }
    let exchangeHandoffInUrl = false;
    try {
      exchangeHandoffInUrl = Boolean(
        new URLSearchParams(window.location.search).get('exchangeToken')
      );
    } catch {
      /* ignore */
    }

    if (
      (isUnauthorized || isForbiddenAuthFailure) &&
      !isPaymentEndpoint &&
      !isAuthEndpoint &&
      !onAuthPage &&
      !authHandoffActive &&
      !exchangeHandoffInUrl
    ) {
      // Only clear auth for true authentication failures.
      // Permission-based 403s (e.g. MEMBER hitting admin-only APIs) should not log the user out.
      console.warn('Authentication failed, redirecting to login', {
        status: error.response?.status,
        errorCode,
      });
      forceClientLogout(errorCode);
    } else if (isPaymentEndpoint && (error.response?.status === 401 || error.response?.status === 403)) {
      // Payment endpoint returned 401/403 - this is expected since it's public
      // Don't clear tokens or redirect, just log it
      console.warn('Payment endpoint returned auth error (expected for public endpoints):', url);
    }
    return Promise.reject(error);
  }
);

export interface Collection {
  id: number;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  /** Present when collection is nested under another collection */
  parentCollectionId?: number | null;
  /** requestByRequest | roundRobinDataSet — configured in Collections */
  executionMode?: 'requestByRequest' | 'roundRobinDataSet' | string | null;
  roundRobinIterations?: number | null;
  /** continue | stopFlow | stopIteration */
  roundRobinFailureMode?: 'continue' | 'stopFlow' | 'stopIteration' | string | null;
  roundRobinRetryCount?: number | null;
}

export interface TrashItem {
  id: number;
  name: string;
  workspace: string;
  deletedBy: string;
  deletedOn: string;
  dayOfRemoval: string;
  daysRemaining: number;
  dayOfRemovalEpochMs?: number;
  msUntilRemoval?: number;
  type: 'collection' | 'project' | 'environment';
  /** Set for deleted sub-collections when parent is also in trash. */
  parentCollectionId?: number | null;
}

export interface Request {
  id: number;
  collectionId: number;
  name: string;
  method: string;
  url: string;
  headers: string;
  body?: string | null; // Deprecated: body lives in data sets only
  bodyType: string;
  rawBodySubtype: string;
  authType: string;
  authConfig: string;
  description: string;
  requestBodySchema?: any;
  responseSchema?: any;
  conditionalRulesJson?: string | null;
  assertionRulesJson?: string | null;
  environmentId?: number | null;
  environment?: { id: number; name: string } | null;
  environmentUrl?: string | null;
  /** When true (default), bulk runs use the collection/run-level environment base URL. */
  useRunBaseUrl?: boolean | null;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardSummary {
  totalCollections: number;
  totalSubCollections: number;
  totalRequests: number;
  totalDataSets: number;
  totalAssertions: number;
  totalTestCycles: number;
  responses2xx: number;
  responses3xx: number;
  responses4xx: number;
  responses5xx: number;
  totalOrganizationMembers?: number;
}

export interface Environment {
  id: number;
  name: string;
  description: string;
  url: string;
  baseUrl?: string;
  /** Number of requests referencing this environment (from backend). */
  requestCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface EnvironmentVariable {
  id?: number;
  key: string;
  value: string;
  description?: string;
  extractFromResponse?: boolean;
  extractJsonPath?: string;
  extractHeaderName?: string;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
  /** From backend: environment | project | collection */
  scope?: string;
  environmentId?: number;
  projectId?: number;
  collectionId?: number;
}

export interface Project {
  id: number;
  name: string;
  description: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface RequestHistory {
  id: number;
  requestId: number;
  statusCode: number;
  responseBody: string;
  responseHeaders: string;
  executionTimeMs: number;
  executedAt: string;
}

export interface RequestConfigHistoryItem {
  id: number;
  requestId: number;
  userName: string;
  field: string;
  oldValue: string;
  newValue: string;
  createdAt: string;
}

export interface GlobalHistoryItem {
  id: number;
  accountId: number;
  method: string;
  url: string;
  status: string;
  statusCode: number;
  requestBody: string | null;
  responseBody: string | null;
  requestName?: string | null;
  collectionName?: string | null;
  createdAt: string;
}

export interface ExecutionHistoryPage {
  content: ExecutionHistorySummary[];
  totalElements: number;
  totalPages: number;
  last: boolean;
}

export interface ExecutionHistorySummary {
  id: number;
  requestId: number;
  collectionId?: number;
  userId?: number;
  method: string;
  url: string;
  statusCode: number;
  statusText?: string;
  responseTime?: number;
  responseSize?: number;
  requestName?: string;
  collectionName?: string;
  responseSummary?: string;
  assertionsResultJson?: string;
  createdAt: string;
}

export interface ExecutionHistoryDetail extends ExecutionHistorySummary {
  requestName?: string;
  collectionName?: string;
  requestHeadersJson?: string;
  requestBodyJson?: string;
  responseHeadersJson?: string;
  responseBodyJson?: string;
  executedRequestBody?: string;
  executedResponseBody?: string;
  executedHeaders?: string;
  executedQueryParams?: string;
  executedPathVariables?: string;
  dataSetId?: number;
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  passwordChangedAt?: string;
  organizationId?: number;
  organizationSlug?: string;
  planType?: string;
  role?: string; // User's role in the organization (ADMIN, MEMBER, PROJECT_MANAGER, or ADMIN_MEMBER [Admin + Project Manager])
  profilePictureUrl?: string;
  backgroundImageUrl?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  otp?: string;
  /** When true, server replaces an existing session in one request (avoids login + force-login). */
  replaceExistingSession?: boolean;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  otp?: string;
  organizationName?: string;
  /** STANDARD / PROFESSIONAL defers DB user+org until Stripe payment completes */
  selectedPlan?: string;
  billingCycle?: string;
}

export interface AuthResponse {
  message: string;
  token: string;
  user?: User;
  success: boolean;
  alreadyLoggedIn?: boolean;
  pendingPaidCheckout?: boolean;
  requirePasswordChange?: boolean;
}

/** Parse RFC 5987 / RFC 2183 Content-Disposition filename for blob downloads. */
function parseContentDispositionFilename(header: string | undefined, fallback: string): string {
  if (!header || typeof header !== 'string') return fallback;
  const star = /filename\*=UTF-8''([^;\s]+)/i.exec(header);
  if (star?.[1]) {
    try {
      const n = decodeURIComponent(star[1].replace(/\+/g, ' '));
      if (n) return n;
    } catch {
      /* ignore malformed encoding */
    }
  }
  const quoted = /filename="((?:\\.|[^"\\])*)"/.exec(header);
  if (quoted?.[1]) return quoted[1].replace(/\\"/g, '"');
  const bare = /filename=([^;\s]+)/i.exec(header);
  if (bare?.[1]) return bare[1].replace(/^["']|["']$/g, '');
  return fallback;
}

/** Fallback download filename when the server omits Content-Disposition. */
function defaultLocalProxyArtifactFilename(platformKey: string): string {
  switch (platformKey) {
    case 'windows':
      return 'crud-expert-proxy-setup.exe';
    case 'windows-exe':
      return 'crud-expert-proxy-setup.exe';
    case 'mac-dmg':
      return 'curd-expert-proxy.dmg';
    case 'mac-intel':
      return 'curd-expert-proxy-mac-intel';
    case 'mac-arm':
      return 'curd-expert-proxy-mac-arm';
    case 'linux':
      return 'curd-expert-proxy-linux';
    default:
      return 'download';
  }
}

const API_ERROR_CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

function isApiErrorCode(value: unknown): value is string {
  return typeof value === 'string' && API_ERROR_CODE_PATTERN.test(value.trim());
}

/** Best-effort message from axios/API errors (custom `error`, Spring `message`, or HTTP status). */
export function extractApiErrorMessage(e: unknown): string | null {
  const ax = e as { response?: { data?: unknown; status?: number }; message?: string };
  const d = ax?.response?.data;
  if (d && typeof d === 'object' && d !== null) {
    const o = d as Record<string, unknown>;
    const message = typeof o.message === 'string' ? o.message.trim() : '';
    const error = typeof o.error === 'string' ? o.error.trim() : '';
    const errorCode = typeof o.errorCode === 'string' ? o.errorCode.trim() : '';

    // Backend often returns machine codes in `error` / `errorCode` and the readable text in `message`.
    if (message && (isApiErrorCode(error) || isApiErrorCode(errorCode))) {
      return message;
    }
    if (error) return error;
    if (message) return message;
    if (errorCode) return errorCode;
  }
  if (typeof ax?.response?.status === 'number') {
    return `Request failed with status code ${ax.response.status}`;
  }
  if (typeof ax?.message === 'string' && ax.message.trim()) return ax.message.trim();
  return null;
}

export const apiService = {
  // Authentication
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    // Send as x-www-form-urlencoded (same as Postman Body → x-www-form-urlencoded)
    // Format: email=value&password=value with URL encoding (e.g. @ → %40)
    const body = new URLSearchParams();
    body.append('email', credentials.email);
    body.append('password', credentials.password);
    body.append('otp', credentials.otp || '');
    if (credentials.replaceExistingSession !== false) {
      body.append('replaceExistingSession', 'true');
    }
    const response = await apiClient.post('/auth/login', body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },

  forceLogin: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const body = new URLSearchParams();
    body.append('email', credentials.email);
    body.append('password', credentials.password);
    body.append('otp', credentials.otp || '');
    const response = await apiClient.post('/auth/force-login', body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },

  register: async (userData: RegisterRequest): Promise<AuthResponse> => {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  },

  getTestStaticOtpEnabled: async (): Promise<{ testStaticOtpEnabled: boolean }> => {
    const response = await apiClient.get('/auth/test-static-otp-enabled');
    return response.data;
  },

  sendAuthOtp: async (data: {
    email: string;
    purpose: 'REGISTER' | 'LOGIN';
    firstName?: string;
    lastName?: string;
    password?: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/auth/send-auth-otp', data);
    return response.data;
  },

  // Exchange Token - Secure cross-subdomain authentication
  createExchangeToken: async (data: { token: string; organizationSlug: string }): Promise<{ exchangeToken: string; expiresIn: string }> => {
    const response = await apiClient.post('/auth/create-exchange-token', data);
    return response.data;
  },

  exchangeToken: async (exchangeToken: string): Promise<AuthResponse> => {
    const response = await apiClient.post('/auth/exchange-token', { exchangeToken });
    return response.data;
  },

  logout: async (): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },

  /** Lightweight session check; 401 SESSION_REPLACED triggers automatic client logout. */
  pingSession: async (): Promise<void> => {
    await apiClient.get('/user/me');
  },

  // Password Reset
  getSecurityCode: async (): Promise<{ code: string; sessionId: string }> => {
    const response = await apiClient.get('/auth/security-code');
    return response.data;
  },

  forgotPassword: async (data: { email: string }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/auth/forgot-password', data);
    return response.data;
  },

  verifyOtp: async (data: { email: string; otp: string }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/auth/verify-otp', data);
    return response.data;
  },

  resetPassword: async (data: { email: string; otp: string; newPassword: string; confirmPassword: string }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/auth/reset-password', data);
    return response.data;
  },

  // User Profile
  updateProfile: async (profileData: { firstName: string; lastName: string; email: string }): Promise<User> => {
    const response = await apiClient.put('/user-settings/profile', profileData);
    return response.data;
  },

  uploadProfilePicture: async (file: File): Promise<User> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/user-settings/profile-picture', formData);
    return response.data;
  },

  uploadBackgroundImage: async (file: File): Promise<User> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/user-settings/background-image', formData);
    return response.data;
  },

  deleteBackgroundImage: async (): Promise<User> => {
    const response = await apiClient.delete('/user-settings/background-image');
    return response.data;
  },

  changePassword: async (data: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<{
    success: boolean;
    message: string;
    updatedAt?: string;
    passwordChangedAt?: string;
  }> => {
    const response = await apiClient.put('/user-settings/change-password', data);
    return response.data;
  },

  /** Streams a pre-built local proxy binary from the API (Content-Disposition attachment) with server-side download logging. */
  downloadLocalProxyArtifact: async (platformKey: string): Promise<void> => {
    const response = await apiClient.get(`/local-proxy/artifacts/${encodeURIComponent(platformKey)}`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    });
    const fallback = defaultLocalProxyArtifactFilename(platformKey);
    const cd =
      (response.headers['content-disposition'] as string | undefined) ||
      (response.headers['Content-Disposition'] as string | undefined);
    const filename = parseContentDispositionFilename(cd, fallback);
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  // Collections
  getCollections: async (): Promise<Collection[]> => {
    const response = await apiClient.get('/collections');
    return response.data;
  },

  getCollectionsOpenApi: async (): Promise<any> => {
    const response = await apiClient.get('/collections/openapi');
    return response.data;
  },

  importCollectionsOpenApi: async (payload: {
    projectId: number;
    openApiJson: string;
    specFileName?: string;
  }): Promise<any> => {
    try {
      const response = await apiClient.post('/collections/openapi/import', payload);
      notifyDashboardRecentsUpdated();
      return response.data;
    } catch (e: unknown) {
      const data = (e as { response?: { data?: unknown } })?.response?.data;
      if (data && typeof data === 'object' && data !== null && 'success' in (data as object)) {
        return data;
      }
      throw e;
    }
  },

  importCollections: async (
    projectId: number,
    payload: any
  ): Promise<{
    collectionsImported: number;
    requestsImported: number;
    environmentId?: number;
    requestIdMap?: Record<string, number>;
    partialImport?: boolean;
    message?: string;
    collectionsSkippedForPlanLimit?: number;
    requestsSkippedForPlanLimit?: number;
  }> => {
    const response = await apiClient.post(`/collections/import?projectId=${projectId}`, payload, {
      timeout: 300000,
    });
    notifyDashboardRecentsUpdated();
    return response.data;
  },

  getCollection: async (id: number): Promise<Collection> => {
    const response = await apiClient.get(`/collections/${id}`);
    return response.data;
  },

  getCollectionsByProject: async (projectId: number): Promise<Collection[]> => {
    const response = await apiClient.get(`/collections/project/${projectId}`);
    return response.data;
  },

  createCollection: async (collection: Partial<Collection>): Promise<Collection> => {
    const response = await apiClient.post('/collections', collection);
    notifyDashboardRecentsUpdated();
    return response.data;
  },

  updateCollection: async (id: number, collection: Partial<Collection>): Promise<Collection> => {
    const response = await apiClient.put(`/collections/${id}`, collection);
    notifyDashboardRecentsUpdated();
    return response.data;
  },

  deleteCollection: async (id: number): Promise<void> => {
    await apiClient.delete(`/collections/${id}`);
    notifyDashboardRecentsUpdated();
  },

  getTrash: async (): Promise<TrashItem[]> => {
    const response = await apiClient.get('/collections/trash');
    return response.data;
  },

  restoreCollection: async (id: number): Promise<void> => {
    await apiClient.post(`/collections/${id}/restore`);
    notifyDashboardRecentsUpdated();
  },

  permanentDeleteCollection: async (id: number): Promise<void> => {
    await apiClient.delete(`/collections/${id}/permanent`);
    notifyDashboardRecentsUpdated();
  },

  updateCollectionOrder: async (projectId: number, parentCollectionId: number | null, collectionIds: number[]): Promise<void> => {
    const url = parentCollectionId 
      ? `/collections/project/${projectId}/order?parentCollectionId=${parentCollectionId}`
      : `/collections/project/${projectId}/order`;
    await apiClient.put(url, collectionIds);
    notifyDashboardRecentsUpdated();
  },

  /** Persist Complete Execution Results for a collection run (creates a new run; UI loads latest). */
  saveCollectionRun: async (collectionId: number, report: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post(`/collections/${collectionId}/runs`, report);
    return response.data;
  },

  getCollectionRuns: async (collectionId: number): Promise<{ runs: any[] }> => {
    const response = await apiClient.get(`/collections/${collectionId}/runs`);
    return response.data;
  },

  getLatestCollectionRun: async (collectionId: number): Promise<{ run: any | null }> => {
    const response = await apiClient.get(`/collections/${collectionId}/runs/latest`);
    return response.data;
  },

  getCollectionRun: async (collectionId: number, runId: number): Promise<any> => {
    const response = await apiClient.get(`/collections/${collectionId}/runs/${runId}`);
    return response.data;
  },

  // Requests
  getRequests: async (): Promise<Request[]> => {
    const response = await apiClient.get('/requests');
    return response.data;
  },

  getRequestsByCollection: async (collectionId: number): Promise<Request[]> => {
    const response = await apiClient.get(`/requests/collection/${collectionId}`);
    return response.data;
  },

  getRequestsByProject: async (projectId: number): Promise<Request[]> => {
    const response = await apiClient.get(`/requests/project/${projectId}`);
    return response.data;
  },

  getRequest: async (id: number, projectId?: number): Promise<Request> => {
    const response = await apiClient.get(`/requests/${id}`, {
      params: { projectId },
    });
    return response.data;
  },

  createRequest: async (request: Partial<Request>): Promise<Request> => {
    const payload = normalizeRequestSchemaFields(request);
    const response = await apiClient.post('/requests', payload);
    notifyDashboardRecentsUpdated();
    return response.data;
  },

  updateRequest: async (id: number, request: Partial<Request>): Promise<Request> => {
    const payload = normalizeRequestSchemaFields(request);
    const response = await apiClient.put(`/requests/${id}`, payload);
    notifyDashboardRecentsUpdated();
    return response.data;
  },

  deleteRequest: async (id: number): Promise<void> => {
    await apiClient.delete(`/requests/${id}`);
    notifyDashboardRecentsUpdated();
  },

  updateRequestOrder: async (collectionId: number, requestIds: number[]): Promise<void> => {
    await apiClient.put(`/requests/collection/${collectionId}/order`, requestIds);
    notifyDashboardRecentsUpdated();
  },

  executeRequest: async (id: number): Promise<RequestHistory> => {
    const response = await apiClient.post(`/requests/${id}/execute`);
    notifyDashboardRecentsUpdated();
    return response.data;
  },

  getRequestHistory: async (
    id: number,
    limit: number = 10,
    offset: number = 0,
    search: string = '',
    status: string = ''
  ): Promise<ExecutionHistorySummary[]> => {
    const response = await apiClient.get(`/requests/${id}/history`, {
      params: { limit, offset, search, status },
    });
    return response.data;
  },

  getGlobalHistory: async (
    limit: number = 10,
    offset: number = 0,
    search: string = '',
    status: string = ''
  ): Promise<ExecutionHistorySummary[]> => {
    const response = await apiClient.get('/history', {
      params: { limit, offset, search, status },
    });
    return response.data;
  },

  getExecutionHistoryPage: async (
    projectId: number,
    page: number = 0,
    size: number = 20
  ): Promise<ExecutionHistoryPage> => {
    const response = await apiClient.get(`/history/${projectId}`, {
      params: { page, size },
    });
    return response.data;
  },

  getDashboardSummary: async (projectId: number): Promise<DashboardSummary> => {
    const response = await apiClient.get(`/dashboard/summary/${projectId}`);
    return response.data;
  },

  getHistoryDetail: async (historyId: number): Promise<ExecutionHistoryDetail> => {
    const response = await apiClient.get(`/history/${historyId}`);
    return response.data;
  },

  runHistory: async (historyId: number): Promise<ExecutionHistoryDetail> => {
    const response = await apiClient.post(`/history/${historyId}/run`);
    notifyDashboardRecentsUpdated();
    return response.data;
  },

  clearRequestHistory: async (requestId: number): Promise<void> => {
    await apiClient.delete(`/requests/${requestId}/history`);
  },

  clearExecutionHistory: async (projectId?: number): Promise<void> => {
    await apiClient.delete('/history', {
      params: projectId != null ? { projectId } : undefined,
    });
  },

  deleteExecutionHistoryItem: async (historyId: number): Promise<void> => {
    await apiClient.delete(`/history/${historyId}`);
  },

  createExecutionHistory: async (payload: any): Promise<{ success: boolean }> => {
    const response = await apiClient.post('/history', payload);
    notifyDashboardRecentsUpdated();
    return response.data;
  },

  recordApiExecution: async (payload: {
    projectId: number;
    requestId?: number;
    dataSetId?: number | null;
  }): Promise<void> => {
    await apiClient.post('/execute/record', {
      projectId: payload.projectId,
      requestId: payload.requestId ?? 0,
      ...(payload.dataSetId != null && { dataSetId: payload.dataSetId }),
    });
    notifyApiExecutionUsageUpdated();
  },

  execute: async (payload: {
    projectId: number;
    requestId?: number;
    dataSetId?: number | null;
    url: string;
    method: string;
    headers?: Record<string, string> | string;
    body?: string | null;
  }): Promise<{ status: number; statusText: string; headers: Record<string, string>; body: string }> => {
    const headers =
      typeof payload.headers === 'string'
        ? payload.headers
        : JSON.stringify(payload.headers || {});
    const response = await apiClient.post('/execute', {
      projectId: payload.projectId,
      requestId: payload.requestId ?? 0,
      ...(payload.dataSetId != null && { dataSetId: payload.dataSetId }),
      url: payload.url,
      method: payload.method,
      headers,
      body: payload.body ?? null,
    }, {
      // Execute may return the target HTTP status (4xx/5xx) or execution errors in the body — do not throw via axios.
      validateStatus: () => true,
    });
    const data = response.data;
    if (response.status === 400 && data?.error) {
      const limitErr = new Error(String(data.error)) as Error & {
        response?: { data?: { error?: string } };
      };
      limitErr.response = { data: { error: String(data.error) } };
      throw limitErr;
    }
    notifyDashboardRecentsUpdated();
    notifyApiExecutionUsageUpdated();
    return {
      status: data.status ?? 500,
      statusText: data.statusText ?? 'Error',
      headers: data.headers ?? {},
      body: typeof data.body === 'string' ? data.body : JSON.stringify(data.body ?? ''),
    };
  },

  getRequestConfigHistory: async (
    requestId: number,
    limit: number = 20,
    offset: number = 0
  ): Promise<RequestConfigHistoryItem[]> => {
    const response = await apiClient.get(`/requests/${requestId}/config-history`, {
      params: { limit, offset },
    });
    return response.data;
  },

  createRequestConfigHistory: async (
    requestId: number,
    changes: { userName: string; field: string; oldValue: string; newValue: string }[]
  ): Promise<RequestConfigHistoryItem[]> => {
    const response = await apiClient.post(`/requests/${requestId}/config-history`, changes);
    return response.data;
  },

  autoSaveRequestHistory: async (payload: {
    requestId: number;
    userName?: string;
    data: Record<string, any>;
  }): Promise<RequestConfigHistoryItem[]> => {
    const response = await apiClient.post('/history/auto-save', payload);
    return response.data;
  },

  getGlobalHistoryPage: async (
    page: number = 0,
    size: number = 20
  ): Promise<{ content: GlobalHistoryItem[]; last: boolean; number: number }> => {
    const response = await apiClient.get('/global-history', {
      params: { page, size },
    });
    return response.data;
  },

  createGlobalHistory: async (payload: {
    method: string;
    url: string;
    status: string;
    statusCode: number;
    requestBody?: string | null;
    responseBody?: string | null;
    requestName?: string | null;
    collectionName?: string | null;
  }): Promise<GlobalHistoryItem> => {
    const response = await apiClient.post('/global-history', payload);
    return response.data;
  },

  clearGlobalHistory: async (): Promise<{ success: boolean }> => {
    const response = await apiClient.delete('/global-history');
    return response.data;
  },

  // Projects
  getProjects: async (): Promise<Project[]> => {
    const response = await apiClient.get('/projects/my');
    return response.data;
  },

  getProject: async (id: number): Promise<Project> => {
    const response = await apiClient.get(`/projects/${id}`);
    return response.data;
  },

  createProject: async (project: Partial<Project>): Promise<Project> => {
    const response = await apiClient.post('/projects', project);
    return response.data;
  },

  updateProject: async (id: number, project: Partial<Project>): Promise<Project> => {
    const response = await apiClient.put(`/projects/${id}`, project);
    return response.data;
  },

  deleteProject: async (id: number): Promise<void> => {
    await apiClient.delete(`/projects/${id}`);
  },

  restoreProject: async (id: number): Promise<void> => {
    await apiClient.post(`/projects/${id}/restore`);
  },

  permanentDeleteProject: async (id: number): Promise<void> => {
    await apiClient.delete(`/projects/${id}/permanent`);
  },

  // Environments (scoped by project)
  getEnvironments: async (projectId?: number): Promise<Environment[]> => {
    const url = projectId != null ? `/environments?projectId=${projectId}` : '/environments';
    const response = await apiClient.get(url);
    return response.data;
  },

  getEnvironmentsByProject: async (projectId: number): Promise<Environment[]> => {
    const response = await apiClient.get(`/projects/${projectId}/environments`);
    return response.data;
  },

  getEnvironment: async (id: number): Promise<Environment> => {
    const response = await apiClient.get(`/environments/${id}`);
    return response.data;
  },

  createEnvironment: async (projectId: number, environment: Partial<Environment>): Promise<Environment> => {
    const response = await apiClient.post(`/environments?projectId=${projectId}`, environment);
    return response.data;
  },

  updateEnvironment: async (id: number, environment: Partial<Environment>): Promise<Environment> => {
    const response = await apiClient.put(`/environments/${id}`, environment);
    return response.data;
  },

  deleteEnvironment: async (id: number): Promise<void> => {
    await apiClient.delete(`/environments/${id}`);
  },

  restoreEnvironment: async (id: number): Promise<void> => {
    await apiClient.post(`/environments/${id}/restore`);
  },

  permanentDeleteEnvironment: async (id: number): Promise<void> => {
    await apiClient.delete(`/environments/${id}/permanent`);
  },

  // Environment variables (per-environment)
  getEnvironmentVariables: async (environmentId: number): Promise<EnvironmentVariable[]> => {
    try {
      const response = await apiClient.get(`/environments/${environmentId}/variables`);
      return response.data;
    } catch (err: any) {
      const status = err?.response?.status;
      // Some deployments return 400/404/500 when an environment has no variables yet.
      if (status === 400 || status === 404 || status === 500) {
        return [];
      }
      throw err;
    }
  },

  createEnvironmentVariable: async (environmentId: number, variable: { key: string; value: string; description?: string; extractFromResponse?: boolean; extractJsonPath?: string; extractHeaderName?: string }): Promise<EnvironmentVariable> => {
    const response = await apiClient.post(`/environments/${environmentId}/variables`, variable);
    return response.data;
  },

  updateEnvironmentVariable: async (environmentId: number, variableId: number, variable: Partial<EnvironmentVariable>): Promise<EnvironmentVariable> => {
    const response = await apiClient.put(`/environments/${environmentId}/variables/${variableId}`, variable);
    return response.data;
  },

  deleteEnvironmentVariable: async (environmentId: number, variableId: number): Promise<void> => {
    await apiClient.delete(`/environments/${environmentId}/variables/${variableId}`);
  },

  replaceEnvironmentVariables: async (environmentId: number, variables: EnvironmentVariable[]): Promise<EnvironmentVariable[]> => {
    const response = await apiClient.put(`/environments/${environmentId}/variables`, variables);
    return response.data;
  },

  cleanupMirroredEnvironmentVariables: async (
    environmentId: number,
    projectId: number
  ): Promise<{ success: boolean; deletedCount: number; environmentId: number; projectId: number }> => {
    const response = await apiClient.post(
      `/environments/${environmentId}/variables/cleanup-mirrored-project-duplicates`,
      null,
      { params: { projectId } }
    );
    return response.data;
  },

  importSwagger: async (importData: SwaggerImportRequest): Promise<SwaggerImportResponse> => {
    try {
      const response = await apiClient.post('/import/swagger', importData);
      return response.data;
    } catch (e: unknown) {
      const data = (e as { response?: { data?: unknown } })?.response?.data;
      if (data && typeof data === 'object' && data !== null && 'success' in (data as object)) {
        return data as SwaggerImportResponse;
      }
      throw e;
    }
  },

  createSwaggerImportAssertions: async (
    projectId: number,
  ): Promise<{ dataSetsCreated: number; assertionsCreated: number; errors?: string[] }> => {
    const response = await apiClient.post(
      '/import/swagger/assertions',
      null,
      { params: { projectId }, timeout: 300000 },
    );
    return response.data;
  },

  getSwaggerImportProgress: async (
    projectId: number,
  ): Promise<{
    progressPercent: number;
    progressLabel: string;
    dataSetsCreated: number;
    assertionsCreated: number;
  }> => {
    const response = await apiClient.get('/import/swagger/progress', { params: { projectId } });
    return response.data;
  },

  previewSwagger: async (
    projectId: number,
    options: { url?: string; openApiSpec?: string; specFileName?: string }
  ): Promise<SwaggerPreviewData> => {
    const response = await apiClient.post('/import/swagger/preview', { projectId, ...options });
    return response.data;
  },

  /** Phase 1/2/3: Analyze project context for Swagger AI step. Optional swaggerUrl for import env & variables. */
  swaggerAiAnalyze: async (projectId: number, swaggerUrl?: string | null): Promise<SwaggerAiAnalyzeResponse> => {
    const params: { projectId: number; swaggerUrl?: string } = { projectId };
    if (swaggerUrl != null && swaggerUrl.trim() !== '') params.swaggerUrl = swaggerUrl.trim();
    const response = await apiClient.get('/import/swagger/ai/analyze', { params });
    return response.data;
  },

  /** Phase 5: Execute all project requests (in suggested order) and return a summary. */
  swaggerAiRunFullFlow: async (projectId: number): Promise<SwaggerAiRunSummaryResponse> => {
    const response = await apiClient.post('/import/swagger/ai/run-full-flow', null, { params: { projectId } });
    return response.data;
  },

  /** Phase 6: Replace login credentials in request bodies with variable placeholders. */
  swaggerAiApplyCredentialPlaceholders: async (
    projectId: number,
    usernameKey?: string,
    passwordKey?: string
  ): Promise<void> => {
    const params: { projectId: number; usernameKey?: string; passwordKey?: string } = { projectId };
    if (usernameKey) params.usernameKey = usernameKey;
    if (passwordKey) params.passwordKey = passwordKey;
    await apiClient.post('/import/swagger/ai/apply-credential-placeholders', null, { params });
  },

  /** Execute login API with environment variable substitution; returns response for variable suggestions. */
  swaggerAiExecuteLogin: async (
    projectId: number,
    environmentId: number,
    requestId?: number | null
  ): Promise<SwaggerAiExecuteLoginResponse> => {
    const body: { projectId: number; environmentId: number; requestId?: number } = { projectId, environmentId };
    if (requestId != null) body.requestId = requestId;
    const response = await apiClient.post('/import/swagger/ai/execute-login', body);
    return response.data;
  },

  /** Phase 2: Apply suggested ordering for collections and requests. */
  swaggerAiApplyOrdering: async (projectId: number): Promise<void> => {
    await apiClient.post('/import/swagger/ai/apply-order', null, { params: { projectId } });
  },

  /** Apply default headers (Content-Type, Accept) to all requests with body to avoid 415. */
  swaggerAiApplyDefaultHeaders: async (projectId: number): Promise<void> => {
    await apiClient.post('/import/swagger/ai/apply-default-headers', null, { params: { projectId } });
  },

  // AI Agent API
  agentMessage: async (
    request: import('../types/agent').AgentMessageRequest
  ): Promise<import('../types/agent').AgentMessageResponse> => {
    const response = await apiClient.post('/agent/message', request);
    return response.data;
  },
  agentPrepareSingleRequest: async (
    payload: Record<string, unknown>
  ): Promise<import('../types/agent').AgentSingleRequestPrepareResponse> => {
    const response = await apiClient.post('/agent/prepare-single-request', payload);
    return response.data;
  },
  agentGetSession: async (): Promise<import('../types/agent').AgentSessionState | null> => {
    const response = await apiClient.get('/agent/session');
    if (response.status === 204 || !response.data) return null;
    return response.data;
  },

  agentGuideRun: async (
    projectId?: number | null
  ): Promise<{ projectId: number | null; firstCollectionId: number; firstRequestId: number } | null> => {
    const response = await apiClient.post('/agent/guide-run', projectId != null ? { projectId } : {});
    if (response.status === 204 || !response.data) return null;
    return response.data;
  },

  agentGetSwaggerImportJob: async (
    jobId: string
  ): Promise<import('../utils/agentSwaggerImportTracker').AgentSwaggerImportJob | null> => {
    const response = await apiClient.get(`/agent/swagger-import-jobs/${encodeURIComponent(jobId)}`);
    if (response.status === 204 || !response.data) return null;
    return response.data;
  },

  agentClearSession: async (): Promise<void> => {
    await apiClient.post('/agent/session/clear');
  },

  agentListChats: async (): Promise<import('../types/agent').AgentChatSummary[]> => {
    const response = await apiClient.get('/agent/chats');
    return response.data;
  },

  agentCreateChat: async (title?: string): Promise<import('../types/agent').AgentChatSummary> => {
    const response = await apiClient.post('/agent/chats', null, {
      params: title ? { title } : {},
    });
    return response.data;
  },

  agentGetChatHistory: async (
    chatId: number,
    options?: { limit?: number; beforeId?: number }
  ): Promise<import('../types/agent').AgentChatHistoryResponse> => {
    const params: Record<string, number> = {};
    if (options?.limit != null) params.limit = options.limit;
    if (options?.beforeId != null) params.beforeId = options.beforeId;
    const response = await apiClient.get(`/agent/chats/${chatId}`, { params });
    return response.data;
  },

  agentRenameChat: async (
    chatId: number,
    title: string
  ): Promise<import('../types/agent').AgentChatSummary> => {
    const response = await apiClient.patch(`/agent/chats/${chatId}`, { title });
    return response.data;
  },

  agentDeleteChat: async (chatId: number): Promise<void> => {
    await apiClient.delete(`/agent/chats/${chatId}`);
  },

  // Data Sets API
  getDataSetsByRequest: async (requestId: number): Promise<RequestDataSet[]> => {
    const response = await apiClient.get(`/data-sets/request/${requestId}`);
    return response.data;
  },

  getDataSetsByRequestAndMethod: async (requestId: number, method: string): Promise<RequestDataSet[]> => {
    const response = await apiClient.get(`/data-sets/request/${requestId}/method/${method}`);
    return response.data;
  },

  getDataSet: async (dataSetId: number): Promise<RequestDataSet> => {
    const response = await apiClient.get(`/data-sets/${dataSetId}`);
    return response.data;
  },

  createDataSet: async (dataSet: RequestDataSet): Promise<RequestDataSet> => {
    const response = await apiClient.post('/data-sets', dataSet);
    await notifyTestPlanDataSetsUpdated();
    return response.data;
  },

  createBulkDataSets: async (requestId: number, dataSets: RequestDataSet[]): Promise<RequestDataSet[]> => {
    if (!Array.isArray(dataSets) || dataSets.length === 0) return [];

    // Send in batches to avoid freezing UI/network with a single huge payload.
    const created: RequestDataSet[] = [];
    for (let i = 0; i < dataSets.length; i += BULK_DATASET_CHUNK_SIZE) {
      const chunk = dataSets.slice(i, i + BULK_DATASET_CHUNK_SIZE);
      const response = await apiClient.post(`/data-sets/request/${requestId}/bulk`, chunk);
      const chunkCreated = Array.isArray(response.data) ? response.data : [];
      created.push(...chunkCreated);
      // Backend may truncate when plan caps are reached.
      if (chunkCreated.length < chunk.length) break;
    }
    if (created.length > 0) {
      await notifyTestPlanDataSetsUpdated();
    }
    return created;
  },

  generateBulkDataSetsWithFaker: async (
    requestId: number,
    count: number,
    onProgress?: (progress: { created: number; requested: number }) => void
  ): Promise<RequestDataSet[]> => {
    if (!Number.isFinite(count) || count <= 0) return [];

    // Generate in chunks to avoid UI/backend instability for large requests.
    const created: RequestDataSet[] = [];
    let remaining = Math.floor(count);
    const requested = Math.floor(count);

    onProgress?.({ created: 0, requested });

    while (remaining > 0) {
      const chunkCount = Math.min(remaining, BULK_DATASET_CHUNK_SIZE);
      const response = await apiClient.post(`/data-sets/request/${requestId}/generate-bulk`, { count: chunkCount });
      const chunkCreated = Array.isArray(response.data) ? response.data : [];
      created.push(...chunkCreated);
      onProgress?.({ created: created.length, requested });
      remaining -= chunkCount;

      // If backend returns fewer than requested (e.g., plan limit), stop further calls.
      if (chunkCreated.length < chunkCount) break;

      // Yield to event loop between batches to keep UI responsive.
      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    return created;
  },

  generateDataSetsWithAI: async (
    requestId: number,
    prompt: string,
    positiveCount: number = 3,
    negativeCount: number = 2,
    onProgress?: (progress: { created: number; requested: number; message?: string }) => void
  ): Promise<RequestDataSet[]> => {
    const body: { prompt: string; positiveCount: number; negativeCount: number } = {
      prompt,
      positiveCount,
      negativeCount,
    };
    const startResponse = await apiClient.post<{ jobId: string; status: string }>(
      `/data-sets/request/${requestId}/generate-with-ai`,
      body,
      { timeout: 60000 }
    );
    const jobId = startResponse.data?.jobId;
    if (!jobId) {
      throw new Error('AI generation did not return a job id');
    }
    const pollMs = 2000;
    const maxWaitMs = 600000;
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      const statusResponse = await apiClient.get<{
        status: string;
        dataSets?: RequestDataSet[];
        error?: string;
        message?: string;
        progressCreated?: number;
        progressRequested?: number;
        progressMessage?: string;
      }>(`/data-sets/request/${requestId}/generate-with-ai/jobs/${jobId}`, {
        timeout: 60000,
      });
      const progressCreated = statusResponse.data?.progressCreated;
      const progressRequested = statusResponse.data?.progressRequested;
      if (
        onProgress &&
        progressCreated != null &&
        progressRequested != null &&
        progressRequested > 0
      ) {
        onProgress({
          created: progressCreated,
          requested: progressRequested,
          message: statusResponse.data?.progressMessage,
        });
      }
      const status = (statusResponse.data?.status || '').toUpperCase();
      if (status === 'COMPLETED') {
        return statusResponse.data?.dataSets ?? [];
      }
      if (status === 'FAILED') {
        const msg =
          statusResponse.data?.message ||
          statusResponse.data?.error ||
          'AI data set generation failed';
        throw new Error(msg);
      }
      await new Promise((resolve) => setTimeout(resolve, pollMs));
    }
    throw new Error('AI data set generation timed out while waiting for results');
  },

  updateDataSet: async (dataSetId: number, dataSet: Partial<RequestDataSet>): Promise<RequestDataSet> => {
    const response = await apiClient.put(`/data-sets/${dataSetId}`, dataSet);
    return response.data;
  },

  deleteDataSet: async (dataSetId: number): Promise<void> => {
    await apiClient.delete(`/data-sets/${dataSetId}`);
    await notifyTestPlanDataSetsUpdated();
  },

  deleteBulkDataSets: async (dataSetIds: number[]): Promise<void> => {
    if (!Array.isArray(dataSetIds) || dataSetIds.length === 0) return;
    const ids = dataSetIds.filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length === 0) return;
    for (let i = 0; i < ids.length; i += BULK_DATASET_CHUNK_SIZE) {
      const chunk = ids.slice(i, i + BULK_DATASET_CHUNK_SIZE);
      await apiClient.delete('/data-sets/bulk', { data: { dataSetIds: chunk } });
    }
    await notifyTestPlanDataSetsUpdated();
  },

  executeDataSet: async (dataSetId: number): Promise<RequestDataSetResult> => {
    const response = await apiClient.post(`/data-sets/${dataSetId}/execute`);
    notifyDashboardRecentsUpdated();
    return response.data;
  },

  getDataSetResults: async (dataSetId: number): Promise<RequestDataSetResult[]> => {
    const response = await apiClient.get(`/data-sets/${dataSetId}/results`);
    return response.data;
  },

  getLatestResult: async (dataSetId: number): Promise<RequestDataSetResult | null> => {
    const response = await apiClient.get(`/data-sets/${dataSetId}/results/latest`);
    return response.data;
  },

  /** Bulk fetch latest results for all data sets of a request (avoids N+1) */
  getLatestResultsBulk: async (requestId: number): Promise<RequestDataSetResult[]> => {
    const response = await apiClient.get(`/data-sets/request/${requestId}/results/latest-bulk`);
    return response.data ?? [];
  },

  saveDataSetResult: async (dataSetId: number, result: Partial<RequestDataSetResult>): Promise<RequestDataSetResult> => {
    const response = await apiClient.post(`/data-sets/${dataSetId}/results`, result);
    window.dispatchEvent(new CustomEvent('global-history-updated'));
    notifyDashboardRecentsUpdated();
    return response.data;
  },

  // Assertions
  getAssertionsByRequest: async (requestId: number): Promise<RequestAssertion[]> => {
    try {
      const response = await apiClient.get(`/assertions/request/${requestId}`);
      return normalizeAssertionList(response.data);
    } catch (e: unknown) {
      const msg = extractApiErrorMessage(e);
      throw msg ? new Error(msg) : e;
    }
  },

  getAssertionsByDataSet: async (dataSetId: number): Promise<RequestAssertion[]> => {
    try {
      const response = await apiClient.get(`/assertions/dataset/${dataSetId}`);
      return normalizeAssertionList(response.data);
    } catch (e: unknown) {
      const msg = extractApiErrorMessage(e);
      throw msg ? new Error(msg) : e;
    }
  },

  createAssertion: async (assertion: Partial<RequestAssertion>): Promise<RequestAssertion> => {
    try {
      const response = await apiClient.post('/assertions', assertion);
      return normalizeAssertion(response.data ?? {});
    } catch (e: unknown) {
      const msg = extractApiErrorMessage(e);
      throw msg ? new Error(msg) : e;
    }
  },

  updateAssertion: async (id: number, assertion: Partial<RequestAssertion>): Promise<RequestAssertion> => {
    try {
      const response = await apiClient.put(`/assertions/${id}`, assertion);
      return normalizeAssertion(response.data ?? {});
    } catch (e: unknown) {
      const msg = extractApiErrorMessage(e);
      throw msg ? new Error(msg) : e;
    }
  },

  deleteAssertion: async (id: number): Promise<void> => {
    try {
      await apiClient.delete(`/assertions/${id}`);
    } catch (e: unknown) {
      const msg = extractApiErrorMessage(e);
      throw msg ? new Error(msg) : e;
    }
  },

  validateRequestAssertions: async (requestId: number, validationRequest: AssertionValidationRequest): Promise<RequestAssertion[]> => {
    const response = await apiClient.post(`/assertions/request/${requestId}/validate`, validationRequest);
    return normalizeAssertionList(response.data);
  },

  validateDataSetAssertions: async (dataSetId: number, validationRequest: AssertionValidationRequest): Promise<RequestAssertion[]> => {
    const response = await apiClient.post(`/assertions/dataset/${dataSetId}/validate`, validationRequest);
    return normalizeAssertionList(response.data);
  },

  generateSuggestedAssertions: async (suggestionRequest: AssertionSuggestionRequest): Promise<RequestAssertion[]> => {
    const response = await apiClient.post('/assertions/suggest', suggestionRequest);
    return normalizeAssertionList(response.data);
  },

  // Generic methods for custom endpoints
  get: async (url: string, config?: any) => {
    const response = await apiClient.get(url, config);
    return response;
  },
  post: async (url: string, data?: any, config?: any) => {
    const response = await apiClient.post(url, data, config);
    return response;
  },
  put: async (url: string, data?: any, config?: any) => {
    const response = await apiClient.put(url, data, config);
    return response;
  },
  delete: async (url: string, config?: any) => {
    const response = await apiClient.delete(url, config);
    return response;
  },

  // Variables API
  getVariablesByRequest: async (requestId: number): Promise<any[]> => {
    try {
      const response = await apiClient.get(`/variables/request/${requestId}`);
      return mapRequestVariableList(response.data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400 || status === 404) {
        return [];
      }
      throw err;
    }
  },

  getRequestScopedVariablesByRequest: async (requestId: number): Promise<any[]> => {
    try {
      const response = await apiClient.get(`/variables/request/${requestId}/request-scoped`);
      return mapRequestVariableList(response.data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400 || status === 404) {
        return [];
      }
      throw err;
    }
  },

  getVariablesByCollection: async (collectionId: number): Promise<any[]> => {
    try {
      const response = await apiClient.get(`/variables/collection/${collectionId}`);
      return mapRequestVariableList(response.data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400 || status === 404) {
        return [];
      }
      throw err;
    }
  },

  getRequestScopedVariablesByCollection: async (collectionId: number): Promise<any[]> => {
    try {
      const response = await apiClient.get(`/variables/collection/${collectionId}/request-scoped`);
      return mapRequestVariableList(response.data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400 || status === 404) {
        return [];
      }
      throw err;
    }
  },

  /** All REQUEST-scoped variables for every request in the project. */
  getRequestScopedVariablesByProject: async (projectId: number): Promise<any[]> => {
    try {
      const response = await apiClient.get(`/variables/project/${projectId}/request-scoped`);
      return mapRequestVariableList(response.data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400 || status === 404) {
        return [];
      }
      throw err;
    }
  },

  getVariablesByProject: async (projectId: number): Promise<any[]> => {
    try {
      const response = await apiClient.get(`/variables/project/${projectId}`);
      return mapRequestVariableList(response.data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400 || status === 404) {
        return [];
      }
      throw err;
    }
  },

  createVariable: async (variable: any): Promise<any> => {
    const response = await apiClient.post('/variables', variable);
    return mapRequestVariableDto(response.data);
  },

  updateVariable: async (id: number, variable: any): Promise<any> => {
    const response = await apiClient.put(`/variables/${id}`, variable);
    return mapRequestVariableDto(response.data);
  },

  saveVariable: async (variable: any): Promise<any> => {
    if (variable?.id !== undefined && variable?.id !== null) {
      const response = await apiClient.put(`/variables/${variable.id}`, variable);
      return mapRequestVariableDto(response.data);
    }
    const response = await apiClient.post('/variables', variable);
    return mapRequestVariableDto(response.data);
  },

  applyVariableAsBearerTokenToAllRequests: async (projectId: number, variableName: string): Promise<void> => {
    await apiClient.post(`/projects/${projectId}/requests/apply-bearer-token`, { variableName });
  },

  applyVariableAsBearerTokenToCollectionRequests: async (collectionId: number, variableName: string): Promise<void> => {
    await apiClient.post(`/collections/${collectionId}/apply-bearer-token`, { variableName });
  },

  assignUserToProject: async (projectId: number, email: string): Promise<{ message?: string; error?: string }> => {
    const response = await apiClient.post(`/projects/${projectId}/assign-user`, { email });
    return response.data;
  },

  getProjectMembers: async (projectId: number): Promise<Array<{
    userId: number;
    name: string;
    email: string;
    projectCount: number;
  }>> => {
    const response = await apiClient.get(`/projects/${projectId}/members`);
    return response.data;
  },

  removeProjectMember: async (projectId: number, userId: number): Promise<{ message?: string; error?: string }> => {
    const response = await apiClient.delete(`/projects/${projectId}/members/${userId}`);
    return response.data;
  },

  getAssignableProjectMembers: async (projectId: number): Promise<string[]> => {
    const response = await apiClient.get(`/projects/${projectId}/assignable-members`);
    return response.data;
  },

  deleteVariable: async (variableId: number): Promise<void> => {
    await apiClient.delete(`/variables/${variableId}`);
  },

  deleteVariablesBulk: async (variableIds: number[]): Promise<void> => {
    await apiClient.delete('/variables/bulk', { data: { variableIds } });
  },

  // Organizations API
  getOrganizations: async (): Promise<any[]> => {
    const response = await apiClient.get('/organizations');
    return response.data;
  },

  getPrimaryOrganization: async (): Promise<any> => {
    const response = await apiClient.get('/organizations/primary');
    return response.data;
  },

  switchOrganization: async (organizationId: number): Promise<AuthResponse> => {
    const response = await apiClient.post(`/organizations/${organizationId}/switch`);
    return response.data;
  },

  createOrganization: async (name: string): Promise<any> => {
    const response = await apiClient.post('/organizations', { name });
    return response.data;
  },

  /** Validate current tenant (subdomain). Returns { valid: true } if registered, { valid: false } only when API returns 404 (org missing). Other errors throw so callers can retry and avoid caching a false negative. */
  validateTenant: async (): Promise<{ valid: boolean }> => {
    try {
      const response = await apiClient.get('/tenant/validate');
      const data = response.data as { valid?: boolean };
      return { valid: data?.valid === true };
    } catch (err: any) {
      if (err.response?.status === 404) return { valid: false };
      throw err;
    }
  },

  submitContactSales: async (payload: {
    name: string;
    email: string;
    companyName: string;
    phoneNumber: string;
    requirements: string;
  }): Promise<{ message: string }> => {
    const response = await apiClient.post('/contact-sales', payload);
    return response.data;
  },

  // Payment API
  createCheckoutSession: async (request: { planType: string; organizationId?: number; billingCycle?: string; returnBaseUrl?: string }): Promise<{
    success: boolean;
    message?: string;
    checkoutUrl?: string;
    sessionId?: string;
  }> => {
    const body = { ...request, returnBaseUrl: request.returnBaseUrl ?? window.location.origin };
    const response = await apiClient.post('/payment/create-checkout-session', body);
    return response.data;
  },

  /** Post-registration paid signup: requires Bearer token; returns Stripe checkout URL as `url` or `checkoutUrl`. */
  createSignupPaymentSession: async (request: {
    email: string;
    plan: string;
    billing: string;
    returnBaseUrl?: string;
  }): Promise<{
    success: boolean;
    message?: string;
    checkoutUrl?: string;
    url?: string;
    sessionId?: string;
  }> => {
    const body = { ...request, returnBaseUrl: request.returnBaseUrl ?? window.location.origin };
    const response = await apiClient.post('/payment/create-session', body);
    return response.data;
  },

  verifySubscriptionByEmail: async (email: string): Promise<{ status: string; reason?: string }> => {
    const response = await apiClient.get('/subscription/verify', {
      params: { email },
    });
    return response.data;
  },

  // Plan upgrade proration (STANDARD -> PROFESSIONAL)
  previewPlanUpgrade: async (billingCycle?: string): Promise<{
    professionalPlanPrice?: number;
    prorationCredit?: number;
    finalPayableAmount?: number;
    planPrice?: number;
    amountDue?: number;
    billingCycle?: string;
    stripeAmountDuePreview?: number;
    stripeProrationTotal?: number;
    error?: string;
  }> => {
    const params: any = {};
    if (billingCycle) {
      params.billingCycle = billingCycle;
    }
    const response = await apiClient.get('/admin/account/subscription/upgrade/preview', {
      params,
    });
    return response.data;
  },

  /** Prorated upgrade calculation: currentPlan, newPlan, remainingCredit, upgradeCost. Does not charge. */
  subscriptionUpgrade: async (newPlanId: string, billingCycle?: string): Promise<{
    currentPlan?: string;
    newPlan?: string;
    remainingCredit?: number;
    upgradeCost?: number;
    error?: string;
  }> => {
    const body: { newPlanId: string; billingCycle?: string } = { newPlanId };
    if (billingCycle) body.billingCycle = billingCycle;
    const response = await apiClient.post('/admin/account/subscription/upgrade', body);
    return response.data;
  },

  confirmPlanUpgrade: async (billingCycle?: string, returnBaseUrl?: string): Promise<{
    message?: string;
    error?: string;
    oldPlan?: string;
    newPlan?: string;
    professionalPlanPrice?: number;
    prorationCredit?: number;
    finalChargedAmount?: number;
    billingCycle?: string;
    stripeInvoiceId?: string;
    stripeSubscriptionId?: string;
    requiresPayment?: boolean;
    paymentUrl?: string;
    checkoutUrl?: string;
    sessionId?: string;
    amountDue?: number;
  }> => {
    const body: any = {};
    if (billingCycle) {
      body.billingCycle = billingCycle;
    }
    if (returnBaseUrl) {
      body.returnBaseUrl = returnBaseUrl;
    }
    const response = await apiClient.post('/admin/account/subscription/upgrade/confirm', body);
    return response.data;
  },

  verifyPayment: async (sessionId: string): Promise<{
    message?: string;
    error?: string;
    organizationId?: string;
    organizationSlug?: string;
    planType?: string;
    sessionId?: string;
    status?: string;
  }> => {
    const response = await apiClient.get(`/payment/success?session_id=${sessionId}`);
    return response.data;
  },

  verifyLicensePayment: async (sessionId: string): Promise<{
    message?: string;
    error?: string;
    organizationId?: string;
    additionalLicenses?: string;
    newCount?: string;
    sessionId?: string;
  }> => {
    // This endpoint is public (no auth required) since Stripe redirects here
    // Make a direct axios call without auth headers to avoid token issues
    const baseUrl = getApiBaseUrl();
    const response = await axios.get(`${baseUrl}/payment/license-success?session_id=${sessionId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      // Don't send Authorization header - endpoint is public
    });
    return response.data;
  },

  getTenantAddons: async (): Promise<{
    error?: string;
    addons?: Array<{
      addonCode: string;
      displayName: string;
      description?: string;
      currentOfferCapacity: number;
      currentOfferPrice: number;
      billingModel: string;
      canPurchase: boolean;
      purchasedCapacity: number;
      used?: number | null;
      limit?: number | null;
      retentionDays?: number | null;
      purchasedRetentionDays?: number | null;
    }>;
  }> => {
    const response = await apiClient.get('/admin/account/tenant-addons');
    return response.data;
  },

  createTenantAddonCheckoutSession: async (
    addonCode: string,
    quantity = 1,
    returnBaseUrl?: string
  ): Promise<{ success: boolean; checkoutUrl?: string; message?: string }> => {
    const response = await apiClient.post('/admin/account/tenant-addons/checkout', {
      addonCode,
      quantity,
      returnBaseUrl: returnBaseUrl || window.location.origin,
    });
    return response.data;
  },

  verifyTenantAddonPayment: async (sessionId: string): Promise<{
    message?: string;
    error?: string;
    organizationId?: string;
    addonCode?: string;
    quantity?: string;
  }> => {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get(`${baseUrl}/payment/tenant-addon-success?session_id=${sessionId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  },

  verifyApiExecutionAddonPayment: async (sessionId: string): Promise<{
    message?: string;
    error?: string;
    organizationId?: string;
    packCount?: string;
    apiExecutionAddonQuota?: string;
  }> => {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get(`${baseUrl}/payment/api-execution-addon-success?session_id=${sessionId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  },

  getApiExecutionAddonUsage: async (): Promise<{
    error?: string;
    planType?: string;
    displayPlan?: string;
    planBaseExecutions?: number | null;
    addonExecutions?: number;
    totalExecutions?: number | null;
    usedExecutions?: number;
    availableExecutions?: number | null;
    billingPeriodStart?: string;
    billingPeriodEnd?: string;
    activeAddonPacks?: number;
    executionsPerPack?: number;
    pricePerPackMonthly?: number;
    canPurchase?: boolean;
    addonUnavailableMessage?: string;
  }> => {
    const response = await apiClient.get('/admin/account/api-execution-addons');
    return response.data;
  },

  previewApiExecutionAddonPurchase: async (packCount: number): Promise<{
    error?: string;
    packCount?: number;
    fullExecutions?: number;
    fullPrice?: number;
    grantedExecutions?: number;
    grantedExecutionsPerPack?: number;
    amountDue?: number;
    billingPeriodEnd?: string;
    autoRenew?: boolean;
    rollover?: boolean;
  }> => {
    const response = await apiClient.post('/admin/account/api-execution-addons/purchase', {
      packCount,
    });
    return response.data;
  },

  getApiExecutionAddonHistory: async (): Promise<{
    history?: Array<{
      id: number;
      changedAt: string;
      previousQuota: number;
      newQuota: number;
      deltaExecutions: number;
      packCount: number;
      reason?: string;
    }>;
    error?: string;
  }> => {
    const response = await apiClient.get('/admin/account/api-execution-addons/history');
    return response.data;
  },

  createApiExecutionAddonCheckoutSession: async (packCount: number): Promise<{
    success?: boolean;
    checkoutUrl?: string;
    message?: string;
    sessionId?: string;
  }> => {
    const returnBaseUrl = typeof window !== 'undefined' ? window.location.origin : undefined;
    const response = await apiClient.post('/admin/account/api-execution-addons/checkout', {
      packCount,
      returnBaseUrl,
    });
    return response.data;
  },

  // Activate FREE plan (no payment needed)
  activateFreePlan: async (organizationId: number): Promise<{
    message?: string;
    error?: string;
    organizationId?: string;
    status?: string;
  }> => {
    const response = await apiClient.post(`/organizations/${organizationId}/activate-free`);
    return response.data;
  },

  // ==================== Admin API - Team Management ====================
  getTeamMembers: async (): Promise<{
    members?: Array<{
      id: number;
      userId: number;
      firstName: string;
      lastName: string;
      email: string;
      role: string;
      status: string;
      joinedAt: string;
      addedByEmail?: string | null;
    }>;
    currentUserCanRemoveMembers?: boolean;
    // Effective allowed email domain for team members:
    // - If organization.allowedEmailDomain is set, that value
    // - Otherwise, the creator's email domain
    allowedEmailDomain?: string;
    error?: string;
  }> => {
    const response = await apiClient.get('/admin/team/members');
    return response.data;
  },

  addTeamMember: async (
    email: string,
    role: string = 'MEMBER',
    options?: { firstName?: string; lastName?: string }
  ): Promise<{
    id?: number;
    userId?: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
    message?: string;
    error?: string;
  }> => {
    // Compatibility for older server enums: some deployments accept ADMIN_MEMBER/ADMIN_PM
    // instead of ADMIN_PROJECT_MANAGER.
    const roleCandidates =
      role === 'ADMIN_PROJECT_MANAGER'
        ? ['ADMIN_PROJECT_MANAGER', 'ADMIN_MEMBER', 'ADMIN_PM']
        : [role];
    let lastError: any = null;
    for (const candidateRole of roleCandidates) {
      try {
        const response = await apiClient.post('/admin/team/members', {
          email,
          role: candidateRole,
          ...(options?.firstName ? { firstName: options.firstName } : {}),
          ...(options?.lastName ? { lastName: options.lastName } : {}),
        });
        return response.data;
      } catch (err: any) {
        lastError = err;
      }
    }
    throw lastError;
  },

  updateMemberRole: async (userId: number, role: string, updates?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  }): Promise<{
    id?: number;
    userId?: number;
    role?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    message?: string;
    error?: string;
  }> => {
    const response = await apiClient.put(`/admin/team/members/${userId}/role`, {
      role,
      ...(updates || {}),
    });
    return response.data;
  },

  removeTeamMember: async (userId: number): Promise<{
    message?: string;
    error?: string;
  }> => {
    const response = await apiClient.delete(`/admin/team/members/${userId}`);
    return response.data;
  },

  // ==================== Admin API - License Management ====================
  getLicenseUsage: async (): Promise<{
    totalLicenses?: number;
    usedLicenses?: number;
    availableLicenses?: number;
    planType?: string;
    displayPlan?: string;
    error?: string;
  }> => {
    const response = await apiClient.get('/admin/account/licenses');
    return response.data;
  },

  getLicenseProrationPreview: async (quantity: number): Promise<{
    plan?: string;
    billingCycle?: string;
    originalPrice?: number;
    remainingBillingTimeSeconds?: number;
    totalBillingTimeSeconds?: number;
    remainingBillingPeriodDays?: number;
    proratedPricePerLicense?: number;
    quantity?: number;
    totalAmount?: number;
    subscriptionStart?: number;
    subscriptionEnd?: number;
    error?: string;
  }> => {
    const response = await apiClient.get('/admin/account/licenses/proration-preview', {
      params: { quantity },
    });
    return response.data;
  },

  purchaseLicenses: async (additionalLicenses: number): Promise<{
    additionalLicenses?: number;
    pricePerLicense?: number;
    totalPrice?: number;
    newTotalLicenses?: number;
    message?: string;
    error?: string;
  }> => {
    const response = await apiClient.post('/admin/account/licenses/purchase', { additionalLicenses });
    return response.data;
  },

  createLicenseCheckoutSession: async (additionalLicenses: number): Promise<{
    success: boolean;
    message?: string;
    checkoutUrl?: string;
    sessionId?: string;
  }> => {
    const response = await apiClient.post('/admin/account/licenses/checkout', {
      additionalLicenses,
      returnBaseUrl: window.location.origin,
    });
    return response.data;
  },

  getLicenseHistory: async (): Promise<{
    history?: Array<{
      id: number;
      changedAt: string;
      previousCount: number;
      newCount: number;
      delta: number;
      reason?: string;
      changedByUserId?: number | null;
    }>;
    error?: string;
  }> => {
    const response = await apiClient.get('/admin/account/licenses/history');
    return response.data;
  },

  // ==================== Admin API - Invoice Management ====================
  getInvoices: async (limit: number = 10): Promise<{
    invoices?: Array<{
      id: string;
      amount: number;
      currency: string;
      status: string;
      invoiceNumber: string;
      date: number;
      dueDate: number | null;
      invoicePdf: string | null;
      hostedInvoiceUrl: string | null;
      receiptPdf?: string | null;
      description?: string | null;
    }>;
    error?: string;
  }> => {
    const response = await apiClient.get(`/admin/account/invoices?limit=${limit}`);
    return response.data;
  },

  getInvoice: async (invoiceId: string): Promise<{
    id?: string;
    amount?: number;
    currency?: string;
    status?: string;
    invoiceNumber?: string;
    date?: number;
    dueDate?: number | null;
    invoicePdf?: string | null;
    hostedInvoiceUrl?: string | null;
    receiptPdf?: string | null;
    lineItems?: Array<{
      description: string;
      amount: number;
      quantity: number;
    }>;
    error?: string;
  }> => {
    const response = await apiClient.get(`/admin/account/invoices/${invoiceId}`);
    return response.data;
  },

  /**
   * Download custom invoice PDF (correct labels, no Pay Online button, plan-specific amount/due date)
   */
  downloadInvoicePdf: async (invoiceId: string, invoiceNumber: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.get(`/admin/account/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
      });
      const blob = response.data as Blob;
      if (blob.type === 'application/json') {
        const text = await blob.text();
        const json = JSON.parse(text);
        return { success: false, error: json.error || 'Download failed' };
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${invoiceNumber || invoiceId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (err: any) {
      let errorMsg = 'Download failed';
      if (err.response?.data) {
        if (err.response.data instanceof Blob && err.response.data.type === 'application/json') {
          try {
            const text = await err.response.data.text();
            const json = JSON.parse(text);
            errorMsg = json.error || errorMsg;
          } catch {
            errorMsg = err.message || errorMsg;
          }
        } else if (typeof err.response.data === 'object' && err.response.data.error) {
          errorMsg = err.response.data.error;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      return { success: false, error: errorMsg };
    }
  },

  downloadReceiptPdf: async (invoiceId: string, invoiceNumber: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.get(`/admin/account/invoices/${invoiceId}/receipt-pdf`, {
        responseType: 'blob',
      });
      const blob = response.data as Blob;
      if (blob.type === 'application/json') {
        const text = await blob.text();
        const json = JSON.parse(text);
        return { success: false, error: json.error || 'Download failed' };
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Receipt-${invoiceNumber || invoiceId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (err: any) {
      let errorMsg = 'Download failed';
      if (err.response?.data) {
        if (err.response.data instanceof Blob && err.response.data.type === 'application/json') {
          try {
            const text = await err.response.data.text();
            const json = JSON.parse(text);
            errorMsg = json.error || errorMsg;
          } catch {
            errorMsg = err.message || errorMsg;
          }
        } else if (typeof err.response.data === 'object' && err.response.data.error) {
          errorMsg = err.response.data.error;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      return { success: false, error: errorMsg };
    }
  },

  // ==================== Admin API - Billing Information ====================
  createBillingPortalSession: async (returnUrl: string = window.location.origin + '/manage-account'): Promise<{
    url?: string;
    error?: string;
  }> => {
    const response = await apiClient.post('/admin/account/billing/portal', { returnUrl });
    return response.data;
  },

  getBillingInfo: async (): Promise<{
    paymentMethods?: Array<{
      id: string;
      // No sensitive payment details - managed securely in Stripe Portal
    }>;
    customer?: {
      exists?: boolean;
      // No sensitive billing details - managed securely in Stripe Portal
    };
    error?: string;
  }> => {
    const response = await apiClient.get('/admin/account/billing/info');
    return response.data;
  },

  // ==================== Admin API - Subscription Management ====================
  getSubscription: async (): Promise<{
    planType?: string;
    displayPlan?: string;
    status?: string;
    billingCycle?: string;
    licenseCount?: number;
    subscriptionId?: string | null;
    subscriptionStatus?: string;
    subscriptionBillingStatus?: string;
    showPaymentWarning?: boolean;
    billingMessage?: string;
    currentPeriodStart?: number;
    currentPeriodEnd?: number;
    /** Same as currentPeriodStart (Unix seconds); Stripe-aligned alias for clients. */
    current_period_start?: number;
    /** Same as currentPeriodEnd (Unix seconds). */
    current_period_end?: number;
    /** Optional formatted validity date returned by some subscription endpoints. */
    validUntil?: string;
    /** Snake_case alias for validUntil. */
    valid_until?: string;
    cancelAtPeriodEnd?: boolean;
    userRequestedCancelAtPeriodEnd?: boolean;
    nextBillingDate?: number;
    nextBillingAmount?: number;
    autopayEnabled?: boolean;
    projectsUsed?: number;
    collectionsUsed?: number;
    requestsUsed?: number;
    datasetsUsed?: number;
    variablesUsed?: number;
    environmentsUsed?: number;
    assertionsUsed?: number;
    testSuitesUsed?: number;
    maxProjects?: number;
    maxCollections?: number;
    maxRequests?: number;
    maxDatasets?: number;
    maxVariables?: number;
    maxEnvironments?: number;
    maxAssertions?: number;
    maxTestSuites?: number;
    message?: string;
    subscriptionError?: string;
    error?: string;
  }> => {
    const response = await apiClient.get('/admin/account/subscription');
    return response.data;
  },

  getSubscriptionView: async (): Promise<{
    showCurrentPlan?: boolean;
    showPricingPlans?: boolean;
    currentPlan?: string;
    status?: string;
    validUntil?: string;
    readOnly?: boolean;
    upgradeAllowed?: boolean;
    plansToShow?: string[];
  }> => {
    const response = await apiClient.get('/subscription/view');
    return response.data;
  },

  getSubscriptionNotifications: async (): Promise<
    Array<{ id: number; message: string; read?: boolean; isRead?: boolean; createdAt?: string }>
  > => {
    const response = await apiClient.get('/subscription/notifications');
    return response.data;
  },

  getSubscriptionDetails: async (): Promise<{
    plan?: string;
    contactSales?: boolean;
    limits?: {
      maxProjects?: number;
      maxCollections?: number;
      maxRequests?: number;
      maxDatasets?: number;
      /** Per-request cap; omitted for FREE/STANDARD/PROFESSIONAL (data sets are capped per project). */
      maxDataSetsPerRequest?: number;
      /** For FREE/STANDARD/PROFESSIONAL: same as maxDatasets (per-project). Other plans: tenant aggregate where applicable. */
      maxDatasetsAllProjects?: number;
      maxAssertions?: number;
      maxVariables?: number;
      maxEnvironments?: number;
      maxTestSuites?: number;
      supportType?: string;
    };
    usage?: {
      projects?: number;
      collections?: number;
      requests?: number;
      datasets?: number;
      assertions?: number;
      variables?: number;
      environments?: number;
      testSuites?: number;
    };
    error?: string;
  }> => {
    const response = await apiClient.get('/subscription/details');
    return response.data;
  },

  getSubscriptionFeatureLimits: async (tenantId?: number): Promise<{
    projects?: string;
    collections?: string;
    requests?: string;
    dataSets?: string;
    variables?: string;
    environments?: string;
    assertions?: string;
    testSuites?: string;
    usageProjects?: number;
    usageCollections?: number;
    usageRequests?: number;
    usageDataSets?: number;
    usageVariables?: number;
    usageEnvironments?: number;
    usageAssertions?: number;
    usageTestSuites?: number;
    error?: string;
  }> => {
    const response = await apiClient.get('/subscription/feature-limits', {
      params: tenantId ? { tenantId } : undefined,
    });
    return response.data;
  },

  getSubscriptionUsage: async (tenantId?: number): Promise<{
    projects?: { used: number; limit: number };
    environments?: { used: number; limit: number };
    executions?: { used: number; limit: number };
    history?: { retentionDays: number };
    error?: string;
  }> => {
    const response = await apiClient.get('/subscription/usage', {
      params: tenantId ? { tenantId } : undefined,
    });
    return response.data;
  },

  getSubscriptionProjects: async (): Promise<Project[]> => {
    const response = await apiClient.get('/subscription/projects');
    return response.data;
  },

  getSubscriptionProjectUsageBreakdown: async (
    projectId: number
  ): Promise<{
    collections?: number;
    requests?: number;
    dataSets?: number;
    variables?: number;
    assertions?: number;
    environments?: number;
    testSuites?: number;
    error?: string;
  }> => {
    const response = await apiClient.get(`/subscription/projects/${projectId}/usage-breakdown`);
    return response.data;
  },

  cancelSubscription: async (immediately: boolean = false): Promise<{
    message?: string;
    subscriptionStatus?: string;
    cancelAtPeriodEnd?: boolean;
    error?: string;
  }> => {
    const response = await apiClient.post('/admin/account/subscription/cancel', { immediately });
    return response.data;
  },

  reactivateSubscription: async (): Promise<{
    message?: string;
    subscriptionStatus?: string;
    error?: string;
  }> => {
    const response = await apiClient.post('/admin/account/subscription/reactivate');
    return response.data;
  },

  resubscribeSubscription: async (): Promise<{
    success?: boolean;
    message?: string;
    checkoutUrl?: string;
    sessionId?: string;
    error?: string;
  }> => {
    const response = await apiClient.post('/admin/account/subscription/resubscribe', {
      returnBaseUrl: window.location.origin,
    });
    return response.data;
  },

  /*
  // Profile & User Settings API
  updateProfile: async (data: { firstName?: string; lastName?: string; email?: string; phone?: string }): Promise<any> => {
    const response = await apiClient.put('/user/profile', data);
    return response.data;
  },*/

  /*changePassword: async (data: { currentPassword: string; newPassword: string }): Promise<any> => {
    const response = await apiClient.post('/user/change-password', data);
    return response.data;
  },*/

  deleteAccount: async (): Promise<any> => {
    const response = await apiClient.delete('/user/account');
    return response.data;
  },

  // Organization Settings API
  updateOrganization: async (organizationId: number, data: { name?: string; slug?: string; description?: string }): Promise<any> => {
    const response = await apiClient.put(`/organizations/${organizationId}`, data);
    return response.data;
  },

  deleteOrganization: async (organizationId: number): Promise<any> => {
    const response = await apiClient.delete(`/organizations/${organizationId}`);
    return response.data;
  },

  transferOwnership: async (organizationId: number, newOwnerEmail: string): Promise<any> => {
    const response = await apiClient.post(`/organizations/${organizationId}/transfer-ownership`, { newOwnerEmail });
    return response.data;
  },

  // Security API
  getActiveSessions: async (): Promise<any[]> => {
    const response = await apiClient.get('/user/sessions');
    return response.data;
  },

  logoutSession: async (sessionId: string): Promise<any> => {
    const response = await apiClient.post(`/user/sessions/${sessionId}/logout`);
    return response.data;
  },

  logoutAllSessions: async (): Promise<any> => {
    const response = await apiClient.post('/user/sessions/logout-all');
    return response.data;
  },

  getLoginHistory: async (): Promise<any[]> => {
    const response = await apiClient.get('/user/login-history');
    return response.data;
  },

  // Help / Documentation API
  getHelpModules: async (): Promise<HelpModule[]> => {
    const response = await apiClient.get<HelpModule[]>('/help/modules');
    return response.data;
  },

  getHelpSubModules: async (moduleId: string): Promise<HelpSubModule[]> => {
    const response = await apiClient.get<HelpSubModule[]>(`/help/modules/${encodeURIComponent(moduleId)}/submodules`);
    return response.data;
  },

  getHelpArticlesForSubModule: async (
    subModuleId: string,
    type?: HelpArticleType
  ): Promise<HelpArticle[]> => {
    const response = await apiClient.get<HelpArticle[]>(`/help/submodules/${encodeURIComponent(subModuleId)}/articles`, {
      params: type ? { type } : undefined,
    });
    return response.data;
  },

  getHelpArticle: async (articleId: string): Promise<HelpArticle> => {
    const response = await apiClient.get<HelpArticle>(`/help/articles/${encodeURIComponent(articleId)}`);
    return response.data;
  },

  searchHelpArticles: async (
    query: string,
    moduleId?: string,
    subModuleId?: string
  ): Promise<HelpArticle[]> => {
    const params: { q: string; moduleId?: string; subModuleId?: string } = { q: query };
    if (moduleId) params.moduleId = moduleId;
    if (subModuleId) params.subModuleId = subModuleId;
    const response = await apiClient.get<HelpArticle[]>('/help/search', { params });
    return response.data;
  },

  askHelpChat: async (
    question: string,
    context?: {
      moduleId?: string;
      subModuleId?: string;
      sessionId?: string;
      strictPageLock?: boolean;
      useConversationMemory?: boolean;
      language?: string;
      promptVariant?: 'A' | 'B';
      pageSnapshotHint?: string;
    }
  ): Promise<HelpChatResponse> => {
    const payload: {
      question: string;
      moduleId?: string;
      subModuleId?: string;
      sessionId?: string;
      strictPageLock?: boolean;
      useConversationMemory?: boolean;
      language?: string;
      promptVariant?: 'A' | 'B';
      pageSnapshotHint?: string;
    } = { question };
    if (context?.moduleId) payload.moduleId = context.moduleId;
    if (context?.subModuleId) payload.subModuleId = context.subModuleId;
    if (context?.sessionId) payload.sessionId = context.sessionId;
    if (typeof context?.strictPageLock === 'boolean') payload.strictPageLock = context.strictPageLock;
    if (typeof context?.useConversationMemory === 'boolean') payload.useConversationMemory = context.useConversationMemory;
    if (context?.language) payload.language = context.language;
    if (context?.promptVariant) payload.promptVariant = context.promptVariant;
    if (context?.pageSnapshotHint) payload.pageSnapshotHint = context.pageSnapshotHint;
    const response = await apiClient.post<HelpChatResponse>('/help/chat', payload);
    return response.data;
  },

  submitHelpChatFeedback: async (payload: {
    sessionId?: string;
    messageId?: number;
    helpful: boolean;
    reasonTag?: string;
    moduleId?: string;
    intentMode?: string;
  }): Promise<void> => {
    await apiClient.post('/help/chat-feedback', payload);
  },

  getHelpTelemetry: async (): Promise<{
    totalQueries: number;
    fallbackRate: number;
    moduleMismatchRate: number;
    satisfactionScore: number;
    topFailedQueries: Array<{ reason: string; count: number }>;
  }> => {
    const response = await apiClient.get('/help/telemetry');
    return response.data;
  },

  // ==================== Super Admin Monitoring APIs ====================
  getAdminDashboardSummary: async (): Promise<any> => {
    const response = await apiClient.get('/admin/dashboard-summary');
    return response.data;
  },

  getAdminLoginHistory: async (params: {
    userId?: number;
    status?: string;
    fromDate?: string;
    toDate?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: string;
  }): Promise<any> => {
    const response = await apiClient.get('/login-history', { params });
    return response.data;
  },

  getAdminActivityLogs: async (params: {
    userId?: number;
    actionType?: string;
    entityType?: string;
    fromDate?: string;
    toDate?: string;
    search?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: string;
  }): Promise<any> => {
    const response = await apiClient.get('/activity-logs', { params });
    return response.data;
  },

  getAdminUserDetails: async (userId: number): Promise<any> => {
    const response = await apiClient.get(`/admin/user/${userId}/details`);
    return response.data;
  },

  getAdminProxyUsage: async (params?: any): Promise<any> => {
    const response = await apiClient.get('/admin/proxy-usage', { params });
    return response.data;
  },

  getAdminAdvancedMonitoringSummary: async (params?: { days?: number }): Promise<any> => {
    const response = await apiClient.get('/admin/advanced-monitoring/summary', { params });
    return response.data;
  },

  getAdminAdvancedMonitoringAlerts: async (params?: { failedLoginThreshold?: number; spikeMultiplier?: number }): Promise<any[]> => {
    const response = await apiClient.get('/admin/advanced-monitoring/alerts', { params });
    return response.data;
  },

  getAdminAlerts: async (params?: { windowHours?: number }): Promise<any[]> => {
    const response = await apiClient.get('/admin/alerts', { params });
    return response.data;
  },

  adminRbacListPermissions: async (): Promise<any[]> => {
    const response = await apiClient.get('/admin/rbac/permissions');
    return response.data;
  },

  adminRbacListRoles: async (params?: { tenantId?: number }): Promise<any[]> => {
    const response = await apiClient.get('/admin/rbac/roles', { params });
    return response.data;
  },

  adminRbacCreateRole: async (payload: { tenantId?: number; name: string; description?: string; accessLevel?: number }): Promise<any> => {
    const response = await apiClient.post('/admin/rbac/roles', payload);
    return response.data;
  },

  adminRbacDeleteRole: async (roleId: number): Promise<void> => {
    await apiClient.delete(`/admin/rbac/roles/${roleId}`);
  },

  adminRbacGetRolePermissions: async (roleId: number): Promise<string[]> => {
    const response = await apiClient.get(`/admin/rbac/roles/${roleId}/permissions`);
    return response.data;
  },

  adminRbacSetRolePermissions: async (roleId: number, permissionKeys: string[]): Promise<void> => {
    await apiClient.put(`/admin/rbac/roles/${roleId}/permissions`, { permissionKeys });
  },

  adminRbacGetRoleUsers: async (roleId: number): Promise<any[]> => {
    const response = await apiClient.get(`/admin/rbac/roles/${roleId}/users`);
    return response.data;
  },

  adminRbacSetRoleUsers: async (roleId: number, userIds: number[]): Promise<void> => {
    await apiClient.put(`/admin/rbac/roles/${roleId}/users`, { userIds });
  },

  getAdminUsers: async (params?: { search?: string }): Promise<any[]> => {
    const response = await apiClient.get('/admin/user-management/users', { params });
    return response.data;
  },

  getAdminUserById: async (userId: number): Promise<any> => {
    const response = await apiClient.get(`/admin/user-management/users/${userId}`);
    return response.data;
  },

  createAdminUser: async (payload: any): Promise<any> => {
    const response = await apiClient.post('/admin/user-management/users', payload);
    return response.data;
  },

  updateAdminUser: async (userId: number, payload: any): Promise<any> => {
    const response = await apiClient.put(`/admin/user-management/users/${userId}`, payload);
    return response.data;
  },

  deleteAdminUser: async (userId: number): Promise<void> => {
    await apiClient.delete(`/admin/user-management/users/${userId}`);
  },

  activateAdminUser: async (userId: number): Promise<any> => {
    const response = await apiClient.post(`/admin/user-management/users/${userId}/activate`);
    return response.data;
  },

  deactivateAdminUser: async (userId: number): Promise<any> => {
    const response = await apiClient.post(`/admin/user-management/users/${userId}/deactivate`);
    return response.data;
  },

  resetAdminUserPassword: async (userId: number, newPassword: string): Promise<void> => {
    await apiClient.post(`/admin/user-management/users/${userId}/reset-password`, { newPassword });
  },

  forceLogoutAdminUser: async (userId: number): Promise<void> => {
    await apiClient.post(`/admin/user-management/users/${userId}/force-logout`);
  },

  getAdminUserRoles: async (): Promise<any[]> => {
    const response = await apiClient.get('/admin/user-management/roles');
    return response.data;
  },

  getAdminTenants: async (params?: { search?: string }): Promise<any[]> => {
    const response = await apiClient.get('/admin/tenant-management/tenants', { params });
    return response.data;
  },

  impersonateAdminTenantUser: async (tenantId: number): Promise<{ tenantSlug: string; exchangeToken: string; userEmail?: string }> => {
    const response = await apiClient.post(`/admin/tenant-management/tenants/${tenantId}/impersonate`);
    return response.data;
  },

  getAdminTenantGrievances: async (params?: { search?: string }): Promise<any[]> => {
    const response = await apiClient.get('/admin/grievance/tenants', { params });
    return response.data;
  },

  getAdminRevenueTenants: async (params?: { search?: string; billingCycle?: 'MONTHLY' | 'YEARLY' }): Promise<any[]> => {
    const response = await apiClient.get('/admin/revenue/tenants', { params });
    return response.data;
  },

  getAdminPlanManagementTenantOverview: async (params?: { search?: string }): Promise<any[]> => {
    const response = await apiClient.get('/admin/plan-management/tenants/overview', { params });
    return response.data;
  },

  updateAdminTenantSubscription: async (
    tenantId: number,
    payload: { planType?: string; billingCycle?: string; licenseCount?: number }
  ): Promise<any> => {
    const response = await apiClient.put(`/admin/plan-management/tenants/${tenantId}/subscription`, payload);
    return response.data;
  },

  getAdminPlanCatalog: async (): Promise<any[]> => {
    const response = await apiClient.get('/admin/plan-management/plans');
    return response.data;
  },

  getAdminPlanCatalogById: async (planId: number): Promise<any> => {
    const response = await apiClient.get(`/admin/plan-management/plans/${planId}`);
    return response.data;
  },

  createAdminPlanCatalog: async (payload: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post('/admin/plan-management/plans', payload);
    return response.data;
  },

  updateAdminPlanCatalog: async (planId: number, payload: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.put(`/admin/plan-management/plans/${planId}`, payload);
    return response.data;
  },

  deleteAdminPlanCatalog: async (planId: number): Promise<void> => {
    await apiClient.delete(`/admin/plan-management/plans/${planId}`);
  },

  updateAdminPlanCatalogFeatures: async (planId: number, limits: Record<string, number>): Promise<any> => {
    const response = await apiClient.put(`/admin/plan-management/plans/${planId}/features`, limits);
    return response.data;
  },

  getAdminPlanFeatureDefinitions: async (): Promise<any[]> => {
    const response = await apiClient.get('/admin/plan-management/features');
    return response.data;
  },

  exportAdminLoginCsv: async (params?: any): Promise<void> => {
    const response = await apiClient.get('/admin/login-history/export.csv', { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'login-history.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  },

  exportAdminActivityCsv: async (params?: any): Promise<void> => {
    const response = await apiClient.get('/admin/activity-logs/export.csv', { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'activity-logs.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  },

  getAdminAuditLogs: async (params: {
    userId?: number;
    actionType?: string;
    entityType?: string;
    fromDate?: string;
    toDate?: string;
    search?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: string;
  }): Promise<any> => {
    const response = await apiClient.get('/audit-logs', { params });
    return response.data;
  },

  exportAdminAuditCsv: async (params?: any): Promise<void> => {
    const response = await apiClient.get('/admin/audit-logs/export.csv', { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audit-logs.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  },

  exportAdminProxyDownloadsCsv: async (params?: { fromDate?: string; toDate?: string }): Promise<void> => {
    const response = await apiClient.get('/admin/reports/proxy-downloads/export.csv', { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'proxy-downloads.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  },

  downloadAdminUsageSummaryJson: async (): Promise<{ data: Blob }> => {
    const response = await apiClient.get('/admin/reports/usage-summary.json', { responseType: 'blob' });
    return response;
  },

  downloadAdminSystemBundleZip: async (params?: { fromDate?: string; toDate?: string }): Promise<{ data: Blob }> => {
    const response = await apiClient.get('/admin/reports/system-bundle.zip', { params, responseType: 'blob' });
    return response;
  },

  getAdminSecurityPolicy: async (): Promise<any> => {
    const response = await apiClient.get('/admin/security-controls');
    return response.data;
  },

  updateAdminSecurityPolicy: async (payload: any): Promise<any> => {
    const response = await apiClient.put('/admin/security-controls', payload);
    return response.data;
  },

  getAdminSystemConfiguration: async (): Promise<any> => {
    const response = await apiClient.get('/admin/system-configuration');
    return response.data;
  },

  updateAdminSystemConfiguration: async (payload: {
    globalSettings?: Record<string, unknown>;
    apiKeys?: Record<string, string>;
    integrations?: Record<string, unknown>;
    featureToggles?: Record<string, boolean>;
  }): Promise<any> => {
    const response = await apiClient.put('/admin/system-configuration', payload);
    return response.data;
  },

  // ==================== Super Admin Project Management APIs ====================
  getAdminProjectManagementProjects: async (includeArchived: boolean = false): Promise<any[]> => {
    const response = await apiClient.get('/admin/project-management/projects', {
      params: { includeArchived },
    });
    return response.data;
  },

  createAdminProject: async (payload: {
    tenantId: number;
    name: string;
    description?: string;
    createdBy: string;
  }): Promise<any> => {
    const response = await apiClient.post('/admin/project-management/projects', payload);
    return response.data;
  },

  updateAdminProject: async (projectId: number, payload: {
    name?: string;
    description?: string;
    createdBy?: string;
  }): Promise<any> => {
    const response = await apiClient.put(`/admin/project-management/projects/${projectId}`, payload);
    return response.data;
  },

  archiveAdminProject: async (projectId: number): Promise<void> => {
    await apiClient.delete(`/admin/project-management/projects/${projectId}`);
  },

  restoreAdminProject: async (projectId: number): Promise<void> => {
    await apiClient.post(`/admin/project-management/projects/${projectId}/restore`);
  },

  getAdminProjectUsage: async (projectId: number): Promise<any> => {
    const response = await apiClient.get(`/admin/project-management/projects/${projectId}/usage`);
    return response.data;
  },

  getAdminProjectAssignedUsers: async (projectId: number): Promise<any[]> => {
    const response = await apiClient.get(`/admin/project-management/projects/${projectId}/assigned-users`);
    return response.data;
  },

  updateAdminProjectAssignedUserRole: async (projectId: number, userId: number, role: string): Promise<void> => {
    await apiClient.put(`/admin/project-management/projects/${projectId}/assigned-users/${userId}/role`, {
      role,
    });
  },

  // ==================== Super Admin Environment Management APIs ====================
  getAdminEnvironmentManagementEnvironments: async (includeArchived: boolean = false): Promise<any[]> => {
    const response = await apiClient.get('/admin/environment-management/environments', {
      params: { includeArchived },
    });
    return response.data;
  },

  createAdminEnvironment: async (payload: {
    projectId: number;
    name: string;
    description?: string;
    url: string;
    environmentType?: string;
    createdBy?: string;
  }): Promise<any> => {
    const response = await apiClient.post('/admin/environment-management/environments', payload);
    return response.data;
  },

  archiveAdminEnvironment: async (environmentId: number): Promise<void> => {
    await apiClient.delete(`/admin/environment-management/environments/${environmentId}`);
  },

  restoreAdminEnvironment: async (environmentId: number): Promise<void> => {
    await apiClient.post(`/admin/environment-management/environments/${environmentId}/restore`);
  },

  getAdminEnvironmentAssignedUsers: async (environmentId: number): Promise<any[]> => {
    const response = await apiClient.get(`/admin/environment-management/environments/${environmentId}/assigned-users`);
    return response.data;
  },

  updateAdminEnvironmentAssignedUserRole: async (environmentId: number, userId: number, role: string): Promise<void> => {
    await apiClient.put(
      `/admin/environment-management/environments/${environmentId}/assigned-users/${userId}/role`,
      { role }
    );
  },
};
 